/**
 * Campaign attribution for the template-signup funnel: harvested off the share
 * URL at CTA-click time, appended to the intent-bearing signup URL, and stashed
 * for the Auth0 legs.
 *
 * Unlike the intent, this is peeked, not consumed — the manual org-name form is
 * a legitimate second reader after a refused silent provision. It is cleared
 * only when an org-create actually succeeds, and expires with the intent's TTL.
 *
 * Attribution rides independently of the intent: a share URL with campaign
 * params carries them whether or not the template CTA is on. When it carries
 * none, the shell synthesizes attribution from the surface itself at CTA-click
 * — see deriveSignupUtm — marked `utm_derived=1` so the server can rank it
 * below the invite-code staging.
 */
import type { OrganizationUtm } from "@cohesive/api-client";

import { TEMPLATE_SIGNUP_INTENT_TTL_MS } from "./templateSignupIntent";

/** Attacker-controllable URL input — cap before it is stashed or sent. */
export const SIGNUP_UTM_MAX_LEN = 200;
/**
 * Looser for a click id: a cut one is a warehouse join key that never matches,
 * and fbclids run past 200 in the wild.
 */
export const SIGNUP_CLICK_ID_MAX_LEN = 512;

const STORAGE_KEY = "cohesive:signup-utm";

/** Marks a synthesized value set. Rides strictly after the frozen field params. */
const DERIVED_PARAM = "utm_derived";

/** Param name ⇄ payload field ⇄ value cap. Additive only: never reorder or rename. */
const UTM_FIELDS = [
  ["utm_source", "source", SIGNUP_UTM_MAX_LEN],
  ["utm_medium", "medium", SIGNUP_UTM_MAX_LEN],
  ["utm_campaign", "campaign", SIGNUP_UTM_MAX_LEN],
  ["utm_content", "content", SIGNUP_UTM_MAX_LEN],
  ["utm_term", "term", SIGNUP_UTM_MAX_LEN],
] as const;

/**
 * The ad platform click ids ride with the utms: they are the warehouse's join
 * key from the org back to the ad click. Not campaign attribution in their own
 * right — platforms append them to organic shares too.
 */
const CLICK_ID_FIELDS = [
  ["gclid", "gclid", SIGNUP_CLICK_ID_MAX_LEN],
  ["fbclid", "fbclid", SIGNUP_CLICK_ID_MAX_LEN],
  ["rdt_cid", "rdt_cid", SIGNUP_CLICK_ID_MAX_LEN],
  ["li_fat_id", "li_fat_id", SIGNUP_CLICK_ID_MAX_LEN],
] as const;

const ATTRIBUTION_FIELDS = [...UTM_FIELDS, ...CLICK_ID_FIELDS] as const;

function cap(value: string, maxLen: number = SIGNUP_UTM_MAX_LEN): string {
  return [...value].slice(0, maxLen).join("");
}

function normalize(search: string | URLSearchParams): OrganizationUtm | null {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;

  const utm: OrganizationUtm = {};
  for (const [param, field, maxLen] of ATTRIBUTION_FIELDS) {
    const value = params.get(param)?.trim();
    if (value) utm[field] = cap(value, maxLen);
  }
  if (Object.keys(utm).length === 0) return null;
  if (params.get(DERIVED_PARAM) === "1") utm.derived = true;
  return utm;
}

/** Reads the live URL by default — the CTA harvests at click time. */
export function harvestSignupUtm(
  search: string | URLSearchParams = typeof window === "undefined"
    ? ""
    : window.location.search,
): OrganizationUtm | null {
  return normalize(search);
}

/**
 * The live URL's attribution over the surface's derived fallback. A harvest
 * carrying only click ids does not suppress the derivation: the derived utms
 * stand and the ids ride on them, as the server ranks the two independently.
 */
export function resolveSignupUtm(
  fallback?: OrganizationUtm | null,
  search?: string | URLSearchParams,
): OrganizationUtm | null {
  const harvested = harvestSignupUtm(search);
  if (!harvested || !fallback) return harvested ?? fallback ?? null;
  if (UTM_FIELDS.some(([, field]) => harvested[field])) return harvested;
  return { ...fallback, ...harvested };
}

/** Reads attribution back off an app-origin URL. */
export function parseSignupUtm(
  search: string | URLSearchParams,
): OrganizationUtm | null {
  return normalize(search);
}

export function appendSignupUtmParams(
  params: URLSearchParams,
  utm?: OrganizationUtm | null,
): void {
  if (!utm) return;
  for (const [param, field] of ATTRIBUTION_FIELDS) {
    const value = utm[field];
    if (value) params.append(param, value);
  }
  if (utm.derived) params.append(DERIVED_PARAM, "1");
}

export function stashSignupUtm(utm: OrganizationUtm | null): void {
  if (!utm) return;
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...utm, ts: Date.now() }),
    );
  } catch {
    // Storage unavailable (private mode / quota) — the redirect still carries
    // the attribution in its query params.
  }
}

/**
 * First-touch capture for an entry that isn't the signup route: attribution
 * rides in on whatever URL the app booted on, and the Auth0 round trip drops
 * the query string long before an org is created. No-op once something is
 * stashed — the campaign that brought the visitor outranks the surface they
 * happened to sign up from later.
 */
export function captureSignupUtm(
  search?: string | URLSearchParams,
  now: number = Date.now(),
): void {
  if (peekSignupUtm(now)) return;
  stashSignupUtm(harvestSignupUtm(search));
}

/** Peek-on-read: the stash survives until an org-create succeeds. */
export function peekSignupUtm(
  now: number = Date.now(),
): OrganizationUtm | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const { ts, ...rest } = parsed as OrganizationUtm & { ts?: number };
    if (typeof ts !== "number" || now - ts > TEMPLATE_SIGNUP_INTENT_TTL_MS) {
      return null;
    }

    const utm: OrganizationUtm = {};
    for (const [, field] of ATTRIBUTION_FIELDS) {
      const value = rest[field];
      if (typeof value === "string" && value) utm[field] = value;
    }
    if (Object.keys(utm).length === 0) return null;
    if (rest.derived === true) utm.derived = true;
    return utm;
  } catch {
    return null;
  }
}

/**
 * Attribution synthesized from the public surface a visitor arrived on, for
 * share URLs that carry no campaign params. Total: the surface alone is enough
 * to be truthful, so a missing campaign or org id just drops that field.
 */
export function deriveSignupUtm(input: {
  surface: "public_canvas" | "public_file";
  /** Canvas UUID or public file id — becomes utm_campaign. */
  campaignId?: string | null;
  /** Publishing org UUID — becomes utm_content (canvas artifacts only). */
  authorOrgId?: string | null;
}): OrganizationUtm {
  const campaign = input.campaignId?.trim();
  const content = input.authorOrgId?.trim();
  return {
    source: input.surface,
    medium: "referral",
    ...(campaign ? { campaign: cap(campaign) } : {}),
    ...(content ? { content: cap(content) } : {}),
    derived: true,
  };
}

export function clearSignupUtm(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

/** Re-exported for shells without an api-client dependency. */
export type { OrganizationUtm } from "@cohesive/api-client";
